import { useEffect, useState } from 'react';
import { applyTheme, readThemeChoice, storeThemeChoice, type ThemeChoice } from '../lib/theme';

const OPTIONS: { value: ThemeChoice; label: string }[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
];

/**
 * Theme picker.
 *
 * Three choices rather than a two-way switch. A toggle forces a person to state a preference they may not
 * have — most want the app to follow their machine, and a switch cannot express that. `System` is the
 * default and stays live: the OS flipping to night mode while the app is open should change it.
 *
 * The theme is already applied before this mounts, by an inline script in index.html. This is the control,
 * not the mechanism — which is why a hard refresh does not flash the wrong theme while React boots.
 */
export function ThemeToggle() {
  const [choice, setChoice] = useState<ThemeChoice>(() => readThemeChoice());

  useEffect(() => {
    applyTheme(choice);
    if (choice !== 'system') return;
    /*
     * Only while following the system. An explicit choice must not be overridden by the OS changing —
     * that is the whole difference between "system" and the other two.
     */
    const mq = window.matchMedia?.('(prefers-color-scheme: light)');
    if (!mq) return;
    const onChange = () => applyTheme('system');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [choice]);

  return (
    <div className="theme-toggle" role="group" aria-label="Color theme">
      {OPTIONS.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`seg-btn${choice === o.value ? ' is-active' : ''}`}
          aria-pressed={choice === o.value}
          onClick={() => { setChoice(o.value); storeThemeChoice(o.value); }}
        >{o.label}</button>
      ))}
    </div>
  );
}
